import { Injectable } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { BehaviorSubject, fromEvent, map, merge, Observable, of } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

/**
 * Telas do telão: ficam dias abertas sem navegar. O LayoutComponent esconde o menu nelas, e este
 * service recarrega nelas assim que a versão nova chega.
 */
export const ROTAS_TELAO = ['/pedido/indicador-producao', '/pedido/indicador-producao-cet'];

interface PWAState {
  updateAvailable: boolean;
  installable: boolean;
  installed: boolean;
  canUpdate: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PwaManagerService {
  private deferredPrompt: any;
  private readonly state = new BehaviorSubject<PWAState>({
    updateAvailable: false,
    installable: false,
    installed: false,
    canUpdate: false
  });

  readonly state$ = this.state.asObservable();
  readonly updateAvailable$ = this.state$.pipe(map(state => state.updateAvailable));
  readonly installable$ = this.state$.pipe(map(state => state.installable));
  readonly installed$ = this.state$.pipe(map(state => state.installed));


  constructor(private swUpdate: SwUpdate) {
    this.initializeUpdateDetection();
    this.initializeInstallDetection();
  }

  private initializeUpdateDetection() {
    if (this.swUpdate.isEnabled) {
      // Verifica atualizações disponíveis
      this.swUpdate.versionUpdates
        .pipe(filter(evt => evt.type === 'VERSION_READY'))
        .subscribe(() => {
          this.updateState({ updateAvailable: true, canUpdate: true });

          // O telão fica dias aberto sem navegar: recarrega assim que a versão nova chega.
          if (ROTAS_TELAO.includes(window.location.pathname)) {
            document.location.reload();
          }
        });

      // Cache do SW perdeu um arquivo que a versão em uso ainda pede: só recarregar resolve.
      this.swUpdate.unrecoverable.subscribe(() => document.location.reload());

      // Verifica por atualizações periodicamente (a cada 5 minutos)
      setInterval(() => {
        this.swUpdate.checkForUpdate();
      }, 5 * 60 * 1000);

      // Verifica imediatamente
      this.swUpdate.checkForUpdate();
    }
  }

  private initializeInstallDetection() {
    // Detecta se o app pode ser instalado
    window.addEventListener('beforeinstallprompt', (e) => {
      console.log('PWA: beforeinstallprompt event fired');
      e.preventDefault();
      this.deferredPrompt = e;
      this.updateState({ installable: true });
    });

    // Detecta se o app foi instalado
    window.addEventListener('appinstalled', () => {
      console.log('PWA: appinstalled event fired');
      this.updateState({ installed: true, installable: false });
      this.deferredPrompt = null;
    });

    // Verifica se já está instalado (modo standalone)
    if (window.matchMedia('(display-mode: standalone)').matches || 
        (window.navigator as any).standalone) {
      console.log('PWA: App is already installed (standalone mode)');
      this.updateState({ installed: true, installable: false });
    } else {
      // Se não está instalado, força checagem após um delay
      setTimeout(() => {
        console.log('PWA: Checking installability after delay');
        this.checkInstallability();
      }, 3000);
    }
  }

  private checkInstallability() {
    // Se o prompt ainda não foi capturado, considera como instalável em localhost
    if (!this.deferredPrompt && !this.state.value.installed) {
      const isLocalhost = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1';
      
      if (isLocalhost) {
        console.log('PWA: Forcing installable state for localhost');
        this.updateState({ installable: true });
      }
    }
  }

  private updateState(newState: Partial<PWAState>) {
    this.state.next({ ...this.state.value, ...newState });
  }

  updateApp(): void {
    if (this.swUpdate.isEnabled && this.state.value.canUpdate) {
      this.swUpdate.activateUpdate().then(() => {
        window.location.reload();
      });
    }
  }

  /**
   * Chamado pelo menu lateral e pelas notificações do header, depois da navegação.
   *
   * O SW serve a versão velha até a página recarregar; com a nova já baixada (VERSION_READY),
   * o recarregamento entrega a nova. Só esses dois disparam porque a rota deles abre do zero,
   * como num F5. Navegação interna pode carregar estado em memória (extras.state,
   * *-state.service) que o recarregamento apagaria.
   *
   * Não protege formulário em edição: só o módulo Chamado tem guard de alterações não salvas.
   * Nas outras telas, trocar de rota pelo menu já descartava o que não foi salvo.
   */
  recarregarSeHouverVersaoNova(): void {
    if (this.swUpdate.isEnabled && this.state.value.canUpdate) {
      document.location.reload();
    }
  }

  async installApp(): Promise<boolean> {
    if (!this.deferredPrompt) {
      // Se não há prompt nativo, mostra instruções manuais
      this.showManualInstallInstructions();
      return false;
    }

    try {
      this.deferredPrompt.prompt();
      const { outcome } = await this.deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        this.updateState({ installed: true, installable: false });
        this.deferredPrompt = null;
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Erro ao instalar PWA:', error);
      this.showManualInstallInstructions();
      return false;
    }
  }

  private showManualInstallInstructions() {
    const userAgent = navigator.userAgent.toLowerCase();
    let instructions = '';

    if (userAgent.includes('chrome') || userAgent.includes('edge')) {
      instructions = 'No Chrome/Edge: Clique no ícone ⚡ ou 📱 na barra de endereços, ou vá em Menu (⋮) > "Instalar Fragmaq App"';
    } else if (userAgent.includes('firefox')) {
      instructions = 'No Firefox: Vá em Menu (☰) > "Instalar esta aplicação"';
    } else if (userAgent.includes('safari')) {
      instructions = 'No Safari: Clique em Compartilhar 📤 > "Adicionar à Tela de Início"';
    } else {
      instructions = 'No seu navegador: Procure por opções de "Instalar App" ou "Adicionar à Tela de Início" no menu';
    }

    alert(`Para instalar o Fragmaq App:\n\n${instructions}\n\nSe a opção não aparecer, tente:\n• Recarregar a página (F5)\n• Limpar dados do site\n• Usar modo incógnito`);
  }

  dismissUpdate(): void {
    this.updateState({ updateAvailable: false, canUpdate: false });
  }

  dismissInstall(): void {
    this.updateState({ installable: false });
  }
}
